// Shared Monaco Editor loader using AMD (vs/loader.js)
// Handles script injection, CSS capture for Shadow DOM, and worker setup

const MONACO_VERSION = '0.45.0';
const MONACO_BASE = `https://cdn.jsdelivr.net/npm/monaco-editor@${MONACO_VERSION}/min/vs`;

/** @type {Promise<{monaco: any, styleSheets: CSSStyleSheet[]}>|null} */
let loadPromise = null;

/**
 * Load Monaco Editor via its AMD loader.
 * Returns the monaco namespace and CSSStyleSheet objects for Shadow DOM adoption.
 * Results are cached after first call.
 * @returns {Promise<{monaco: any, styleSheets: CSSStyleSheet[]}>}
 */
export function loadMonaco() {
  if (!loadPromise) {
    loadPromise = _load();
  }
  return loadPromise;
}

async function _load() {
  // Inject the AMD loader script
  await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${MONACO_BASE}/loader.js`;
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load Monaco loader script'));
    document.head.appendChild(script);
  });

  // Configure and load Monaco via AMD require
  /** @type {any} */
  const req = /** @type {any} */ (window).require;
  req.config({ paths: { vs: MONACO_BASE } });

  const monaco = await new Promise((resolve, reject) => {
    req(['vs/editor/editor.main'], resolve, reject);
  });

  // Monaco's AMD loader injects a <link> for editor.main.css.
  // Fetch it and convert to CSSStyleSheet for Shadow DOM adoption.
  const cssUrl = `${MONACO_BASE}/editor/editor.main.css`;
  const response = await fetch(cssUrl);
  const cssText = await response.text();
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(cssText);

  return { monaco, styleSheets: [sheet] };
}
